import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import App from './App';
import { AuthProvider } from './context/auth';

import './global.css';

import * as Sentry from '@sentry/react';
import DocumentUiProvider from './providers/DocumentUiProvider';
import components from './providers/DocumentUiProvider/components';
import isDebug from './utils/isDebug';
import TelemetryProvider from './providers/TelemetryProvider';
import { scrubSentryBreadcrumb, scrubUrlParams } from './utils/scrubSensitiveParams';

Sentry.init({
  enabled: !isDebug,
  dsn: `${import.meta.env.VITE_SENTRY_DSN}`,
  release: `${import.meta.env.PACKAGE_VERSION}-${import.meta.env.VITE_GIT_COMMIT}`,
  environment: `${import.meta.env.VITE_SENTRY_ENV}`,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ block: ['.directory-person-card'] }),
    Sentry.browserProfilingIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Content search terms are health-adjacent and must not reach error reporting. They
  // are kept out of the URL by the search surface, but still travel as a request
  // parameter that Sentry would otherwise record as an xhr/fetch breadcrumb.
  beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
  beforeSend: (event) => {
    if (event.request?.url) event.request.url = scrubUrlParams(event.request.url);

    return event;
  },
  beforeSendTransaction: (event) => {
    event.spans?.forEach((span) => {
      if (span.description) span.description = scrubUrlParams(span.description);
      if (typeof span.data?.url === 'string') span.data.url = scrubUrlParams(span.data.url);
    });
    return event;
  },
});

const rootElement = document.getElementById('root')!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TelemetryProvider>
          <DocumentUiProvider components={components}>
            <AuthProvider>
              <App />
            </AuthProvider>
          </DocumentUiProvider>
        </TelemetryProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
